import { EMPTY, Observable, MonoTypeOperatorFunction} from 'rxjs';
import { tap, catchError, finalize, take as takeOperator } from 'rxjs/operators';

/**
 * Execute function on first emission
 */
export function onFirst<T>(next: (source: T) => void): MonoTypeOperatorFunction<T> {
    return operatorFactory(next, {executeFirst: true});
}

/**
 * Execute function after first emission
 */
export function afterFirst<T>(next: (source: T) => void): MonoTypeOperatorFunction<T> {
    return operatorFactory(next, {executeFirst: false});
}

const noop = () => {};
type WheneverEndCallback = (err?: any) => void;

interface WheneverEndParameters<T, O> {
    callback?: WheneverEndCallback;
    error?: (err: any, caught?: Observable<T>) => Observable<O> | void;
    take?: number;
}

export function wheneverEnd<T, O = any>(parameters: WheneverEndParameters<T, O> | WheneverEndCallback) {

    // if single callback is provided, transform as object
    if (typeof parameters === 'function'  ) {
        parameters = {callback: parameters};
    }

    // destructure parameters
    const {callback, error, take} = parameters;

    // return new observable
    return (source: Observable<T>): Observable<T> => {
        return new Observable<T>(subscriber => {
            return source
                .pipe(
                    takeOperator(take ?? 1),
                    finalize(callback ?? noop),
                    catchError((err, caught) => {
                        const result = (error ?? noop)(err, caught);
                        return result instanceof Observable ? result : EMPTY;
                    }),
                )
                .subscribe({
                    error: e => subscriber.error(e),
                    complete: () => subscriber.complete()
                });
        });
    };
}

function operatorFactory<T>(next: (source: T) => void, param: {executeFirst: boolean}): MonoTypeOperatorFunction<T> {
    let isFirst = true;
    return (source: Observable<T>) => {
        return source.pipe(
            tap(e => {
                if (isFirst === param.executeFirst) {
                    next(e);
                }
                isFirst = false;
            })
        );
    };
}
